import { PostMessageSender, PostMessageHandlers, PostMessageReply } from '../Shared/PostMessage';
import {
  GetMeetingInformationReplyMessage,
  GetMeetingInformationMessage,
  SetMeetingInformationMessage,
  ClearMeetingInformationMessage,
  GetMeetingInformationMessageType,
  SetMeetingInformationMessageType,
  ClearMeetingInformationMessageType
} from '../MessageTypes/ComponentMeetingInformationProviderMessageTypes';
import { ComponentMeetingInformationProvider, MeetingInformation } from '@fluidx/office-fluid-types';
import { IFluidObject } from '@fluidframework/core-interfaces';

/**
 * Initializes everything needed to handle the `ComponentMeetingInformationProvider` interface outside the iframe
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideComponentMeetingInformationProvider(
  sender: PostMessageSender
): { component: ComponentMeetingInformationProvider } {
  return { component: new HostSideMeetingInformationProviderProxy(sender) };
}

class HostSideMeetingInformationProviderProxy implements ComponentMeetingInformationProvider {
  constructor(private sender: PostMessageSender) {}

  public get ComponentMeetingInformationProvider() {
    return this;
  }

  getMeetingInformation(): Promise<MeetingInformation> {
    const message: GetMeetingInformationMessage = {
      type: GetMeetingInformationMessageType
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not get meeting information. Error: ${response.error}`);
      }
      return (response as GetMeetingInformationReplyMessage).payload;
    });
  }

  setMeetingInformation(information: MeetingInformation): Promise<void> {
    const message: SetMeetingInformationMessage = {
      type: SetMeetingInformationMessageType,
      payload: { information }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not set meeting information. Error: ${response.error}`);
      }
    });
  }

  clearMeetingInformation(meetingInformationKeys?: (keyof MeetingInformation)[]): Promise<void> {
    const message: ClearMeetingInformationMessage = {
      type: ClearMeetingInformationMessageType,
      payload: { meetingInformationKeys }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not clear meeting information. Error: ${response.error}`);
      }
    });
  }
}

/**
 * Initializes everything needed to handle the `ComponentMeetingInformationProvider` interface inside the iframe
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideComponentMeetingInformationProvider(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const meetingInformationProvider = component.ComponentMeetingInformationProvider;

  if (!meetingInformationProvider) {
    return undefined;
  }

  return {
    [GetMeetingInformationMessageType]: async (message) => {
      try {
        const information = await meetingInformationProvider.getMeetingInformation();
        sender.sendReply(message, { payload: information });
      } catch {
        sender.sendReply(message, { error: true });
      }
    },
    [SetMeetingInformationMessageType]: async (message) => {
      try {
        await meetingInformationProvider.setMeetingInformation(
          (message as SetMeetingInformationMessage).payload.information
        );
        sender.sendReply(message, {});
      } catch {
        sender.sendReply(message, { error: true });
      }
    },
    [ClearMeetingInformationMessageType]: async (message) => {
      try {
        await meetingInformationProvider.clearMeetingInformation(
          (message as ClearMeetingInformationMessage).payload.meetingInformationKeys
        );
        sender.sendReply(message, {});
      } catch {
        sender.sendReply(message, { error: true });
      }
    }
  };
}
